import React from 'react';
import { Link } from 'react-router-dom';

const Privacy = () => {
    return (
        <div className="privacy-page">
            {/* Hero Section */}
            <section className="privacy-hero">
                <div className="hero-pattern"></div>
                <div className="hero-content">
                    <h1>
                        <span className="hero-title">Privacy Policy</span>
                    </h1>
                    <p className="hero-description">
                        How we collect, use, and protect your personal information
                    </p>
                    <div className="last-updated">
                        <i className="fas fa-calendar-alt"></i>
                        <span>Last updated: March 2026</span>
                    </div>
                </div>
            </section>

            {/* Main Content */}
            <div className="privacy-container">
                <div className="privacy-grid">
                    {/* Sidebar Navigation */}
                    <aside className="privacy-sidebar">
                        <div className="sidebar-sticky">
                            <h3>Quick Navigation</h3>
                            <ul className="sidebar-nav">
                                <li><a href="#introduction">Introduction</a></li>
                                <li><a href="#information">1. Information We Collect</a></li>
                                <li><a href="#usage">2. How We Use Your Information</a></li>
                                <li><a href="#sharing">3. Information Sharing</a></li>
                                <li><a href="#cookies">4. Cookies & Tracking</a></li>
                                <li><a href="#security">5. Data Security</a></li>
                                <li><a href="#rights">6. Your Rights</a></li>
                                <li><a href="#international">7. International Transfers</a></li>
                                <li><a href="#children">8. Children's Privacy</a></li>
                                <li><a href="#changes">9. Changes to Policy</a></li>
                                <li><a href="#contact">10. Contact Us</a></li>
                            </ul>
                            <div className="sidebar-contact">
                                <p>Questions about privacy?</p>
                                <a href="mailto:privacy@redgotrips.com" className="contact-email">
                                    <i className="fas fa-envelope"></i> privacy@redgotrips.com
                                </a>
                                <a href="tel:+919335444047" className="contact-phone">
                                    <i className="fas fa-phone-alt"></i> +91 9335444047
                                </a>
                            </div>
                        </div>
                    </aside>

                    {/* Main Content Area */}
                    <main className="privacy-main">
                        <div className="privacy-content">
                            {/* Introduction */}
                            <section id="introduction" className="privacy-section">
                                <div className="section-badge">01</div>
                                <h2>Introduction</h2>
                                <div className="section-content">
                                    <p>At RedGoTrip, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services. Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the site.</p>
                                    <p>We reserve the right to make changes to this privacy policy at any time and for any reason. We will alert you about any changes by updating the "Last updated" date of this privacy policy. Any changes or modifications will be effective immediately upon posting the updated privacy policy on the Site, and you waive the right to receive specific notice of each such change or modification.</p>
                                    <div className="note-box">
                                        <i className="fas fa-shield-alt"></i>
                                        <p>We are committed to protecting your personal information and your right to privacy. If you have any questions or concerns about this privacy policy or our practices with regard to your personal information, please contact us at privacy@redgotrips.com.</p>
                                    </div>
                                </div>
                            </section>

                            {/* Section 1 */}
                            <section id="information" className="privacy-section">
                                <div className="section-badge">02</div>
                                <h2>1. Information We Collect</h2>
                                <div className="section-content">
                                    <p>We collect personal information that you voluntarily provide to us when you register on the Site, express an interest in obtaining information about us or our products and services, when you participate in activities on the Site, or otherwise when you contact us.</p>
                                    
                                    <h3>Personal Information You Disclose</h3>
                                    <p>The personal information we collect may include the following:</p>
                                    <ul className="info-list">
                                        <li><i className="fas fa-user"></i> <strong>Identity Data:</strong> First name, last name, username, date of birth, and gender</li>
                                        <li><i className="fas fa-envelope"></i> <strong>Contact Data:</strong> Email address, phone number, and postal address</li>
                                        <li><i className="fas fa-credit-card"></i> <strong>Financial Data:</strong> Payment card details, bank account information, and billing address</li>
                                        <li><i className="fas fa-id-card"></i> <strong>Identification Data:</strong> Passport number, driver's license, or government ID (for travel bookings)</li>
                                        <li><i className="fas fa-utensils"></i> <strong>Preferences:</strong> Dietary restrictions, accessibility needs, and travel preferences</li>
                                        <li><i className="fas fa-comment"></i> <strong>Communication Data:</strong> Messages you send to us, inquiries, and customer support requests</li>
                                    </ul>

                                    <h3>Information Automatically Collected</h3>
                                    <p>When you visit our website, we automatically collect certain information about your device and usage patterns:</p>
                                    <ul className="info-list">
                                        <li><i className="fas fa-globe"></i> <strong>Device Data:</strong> IP address, browser type, operating system, and device type</li>
                                        <li><i className="fas fa-chart-line"></i> <strong>Usage Data:</strong> Pages viewed, time spent on site, click patterns, and referral sources</li>
                                        <li><i className="fas fa-map-marker-alt"></i> <strong>Location Data:</strong> General geographic location based on IP address</li>
                                    </ul>
                                </div>
                            </section>

                            {/* Section 2 */}
                            <section id="usage" className="privacy-section">
                                <div className="section-badge">03</div>
                                <h2>2. How We Use Your Information</h2>
                                <div className="section-content">
                                    <p>We use your personal information for the following purposes:</p>
                                    
                                    <div className="usage-grid">
                                        <div className="usage-card">
                                            <i className="fas fa-check-circle"></i>
                                            <h4>Booking Processing</h4>
                                            <p>To process and manage your travel bookings, send confirmations, and provide customer support.</p>
                                        </div>
                                        <div className="usage-card">
                                            <i className="fas fa-check-circle"></i>
                                            <h4>Communication</h4>
                                            <p>To respond to your inquiries, send service updates, and provide important information about your bookings.</p>
                                        </div>
                                        <div className="usage-card">
                                            <i className="fas fa-check-circle"></i>
                                            <h4>Personalization</h4>
                                            <p>To personalize your experience and provide content and offers relevant to your interests.</p>
                                        </div>
                                        <div className="usage-card">
                                            <i className="fas fa-check-circle"></i>
                                            <h4>Improvement</h4>
                                            <p>To analyze usage patterns and improve our website, products, and services.</p>
                                        </div>
                                        <div className="usage-card">
                                            <i className="fas fa-check-circle"></i>
                                            <h4>Marketing</h4>
                                            <p>To send you promotional materials and offers (with your consent where required by law).</p>
                                        </div>
                                        <div className="usage-card">
                                            <i className="fas fa-check-circle"></i>
                                            <h4>Legal Compliance</h4>
                                            <p>To comply with legal obligations and enforce our terms and conditions.</p>
                                        </div>
                                    </div>
                                </div>
                            </section>

                            {/* Section 3 */}
                            <section id="sharing" className="privacy-section">
                                <div className="section-badge">04</div>
                                <h2>3. Information Sharing</h2>
                                <div className="section-content">
                                    <p>We may share your information with the following third parties:</p>
                                    
                                    <ul className="sharing-list">
                                        <li>
                                            <i className="fas fa-hotel"></i>
                                            <div>
                                                <strong>Travel Service Providers:</strong> Hotels, airlines, transport companies, and tour operators to fulfill your bookings.
                                            </div>
                                        </li>
                                        <li>
                                            <i className="fas fa-credit-card"></i>
                                            <div>
                                                <strong>Payment Processors:</strong> To process your payments securely (Razorpay, banks, etc.).
                                            </div>
                                        </li>
                                        <li>
                                            <i className="fas fa-chart-bar"></i>
                                            <div>
                                                <strong>Analytics Partners:</strong> Google Analytics and other tools to analyze website usage.
                                            </div>
                                        </li>
                                        <li>
                                            <i className="fas fa-gavel"></i>
                                            <div>
                                                <strong>Legal Authorities:</strong> When required by law or to protect our rights and safety.
                                            </div>
                                        </li>
                                    </ul>
                                    
                                    <div className="important-box">
                                        <i className="fas fa-exclamation-triangle"></i>
                                        <div>
                                            <h4>Important:</h4>
                                            <p>We do NOT sell your personal information to third parties for marketing purposes.</p>
                                        </div>
                                    </div>
                                </div>
                            </section>

                            {/* Section 4 */}
                            <section id="cookies" className="privacy-section">
                                <div className="section-badge">05</div>
                                <h2>4. Cookies & Tracking</h2>
                                <div className="section-content">
                                    <p>We use cookies and similar tracking technologies to enhance your browsing experience and analyze website traffic.</p>
                                    
                                    <div className="cookies-grid">
                                        <div className="cookie-card">
                                            <h4>Essential Cookies</h4>
                                            <p>Required for basic website functionality and security.</p>
                                            <span className="cookie-badge">Always Active</span>
                                        </div>
                                        <div className="cookie-card">
                                            <h4>Functional Cookies</h4>
                                            <p>Remember your preferences and settings.</p>
                                        </div>
                                        <div className="cookie-card">
                                            <h4>Analytics Cookies</h4>
                                            <p>Help us understand how visitors use our site.</p>
                                        </div>
                                        <div className="cookie-card">
                                            <h4>Marketing Cookies</h4>
                                            <p>Used to deliver relevant advertisements.</p>
                                        </div>
                                    </div>
                                    
                                    <p className="mt-4">You can control cookies through your browser settings. However, disabling certain cookies may affect website functionality.</p>
                                </div>
                            </section>

                            {/* Section 5 */}
                            <section id="security" className="privacy-section">
                                <div className="section-badge">06</div>
                                <h2>5. Data Security</h2>
                                <div className="section-content">
                                    <p>We implement comprehensive security measures to protect your personal information:</p>
                                    
                                    <div className="security-features">
                                        <div className="security-item">
                                            <i className="fas fa-lock"></i>
                                            <span>256-bit SSL Encryption</span>
                                        </div>
                                        <div className="security-item">
                                            <i className="fas fa-shield-alt"></i>
                                            <span>PCI DSS Compliant</span>
                                        </div>
                                        <div className="security-item">
                                            <i className="fas fa-key"></i>
                                            <span>Secure Authentication</span>
                                        </div>
                                        <div className="security-item">
                                            <i className="fas fa-server"></i>
                                            <span>Regular Security Audits</span>
                                        </div>
                                        <div className="security-item">
                                            <i className="fas fa-user-secret"></i>
                                            <span>Access Controls</span>
                                        </div>
                                        <div className="security-item">
                                            <i className="fas fa-database"></i>
                                            <span>Secure Data Storage</span>
                                        </div>
                                    </div>
                                    
                                    <p className="mt-4">While we strive to protect your information, no method of transmission over the Internet is 100% secure. We cannot guarantee absolute security, but we continuously work to maintain best practices.</p>
                                </div>
                            </section>

                            {/* Section 6 */}
                            <section id="rights" className="privacy-section">
                                <div className="section-badge">07</div>
                                <h2>6. Your Rights</h2>
                                <div className="section-content">
                                    <p>Depending on your location, you may have the following rights regarding your personal information:</p>
                                    
                                    <ul className="rights-list">
                                        <li><i className="fas fa-check-circle"></i> <strong>Access:</strong> Request a copy of your personal information</li>
                                        <li><i className="fas fa-check-circle"></i> <strong>Correction:</strong> Update or correct inaccurate information</li>
                                        <li><i className="fas fa-check-circle"></i> <strong>Deletion:</strong> Request deletion of your information ("Right to be Forgotten")</li>
                                        <li><i className="fas fa-check-circle"></i> <strong>Portability:</strong> Receive your data in a portable format</li>
                                        <li><i className="fas fa-check-circle"></i> <strong>Restriction:</strong> Limit how we use your information</li>
                                        <li><i className="fas fa-check-circle"></i> <strong>Objection:</strong> Object to certain types of processing</li>
                                        <li><i className="fas fa-check-circle"></i> <strong>Withdraw Consent:</strong> Withdraw consent at any time</li>
                                    </ul>
                                    
                                    <div className="rights-cta">
                                        <p>To exercise your rights, contact us at:</p>
                                        <a href="mailto:privacy@redgotrips.com" className="btn-outline">
                                            <i className="fas fa-envelope"></i> privacy@redgotrips.com
                                        </a>
                                    </div>
                                </div>
                            </section>

                            {/* Section 7 */}
                            <section id="international" className="privacy-section">
                                <div className="section-badge">08</div>
                                <h2>7. International Data Transfers</h2>
                                <div className="section-content">
                                    <p>Your information may be transferred to and processed in countries other than your own. These countries may have data protection laws different from your jurisdiction.</p>
                                    <p>When we transfer your data internationally, we implement appropriate safeguards, including:</p>
                                    <ul>
                                        <li>Standard contractual clauses approved by the European Commission</li>
                                        <li>Binding corporate rules</li>
                                        <li>Certification under privacy frameworks (such as Data Privacy Framework)</li>
                                    </ul>
                                </div>
                            </section>

                            {/* Section 8 */}
                            <section id="children" className="privacy-section">
                                <div className="section-badge">09</div>
                                <h2>8. Children's Privacy</h2>
                                <div className="section-content">
                                    <p>Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If you become aware that a child has provided us with personal information, please contact us immediately.</p>
                                </div>
                            </section>

                            {/* Section 9 */}
                            <section id="changes" className="privacy-section">
                                <div className="section-badge">10</div>
                                <h2>9. Changes to This Policy</h2>
                                <div className="section-content">
                                    <p>We may update this privacy policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date.</p>
                                    <p>We encourage you to review this privacy policy periodically for any changes. Changes to this privacy policy are effective when they are posted on this page.</p>
                                </div>
                            </section>

                            {/* Section 10 */}
                            <section id="contact" className="privacy-section">
                                <div className="section-badge">11</div>
                                <h2>10. Contact Us</h2>
                                <div className="section-content">
                                    <p>If you have questions or concerns about this privacy policy or our data practices, please contact us:</p>
                                    
                                    <div className="contact-details">
                                        <div className="contact-item">
                                            <i className="fas fa-envelope"></i>
                                            <div>
                                                <strong>Email:</strong>
                                                <a href="mailto:privacy@redgotrips.com">privacy@redgotrips.com</a>
                                            </div>
                                        </div>
                                        <div className="contact-item">
                                            <i className="fas fa-phone-alt"></i>
                                            <div>
                                                <strong>Phone:</strong>
                                                <a href="tel:+919335444047">+91 9335444047</a>
                                            </div>
                                        </div>
                                        <div className="contact-item">
                                            <i className="fas fa-map-marker-alt"></i>
                                            <div>
                                                <strong>Address:</strong>
                                                <address>
                                                    RedGoTrip Pvt. Ltd.<br />
                                                    Dal Lake Road, Boulevard<br />
                                                    Srinagar, Jammu & Kashmir<br />
                                                    India - 190001
                                                </address>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </section>

                            {/* GDPR Compliance Notice */}
                            <div className="gdpr-notice">
                                <i className="fab fa-cc-eu"></i>
                                <div>
                                    <h4>GDPR Compliance</h4>
                                    <p>For users in the European Economic Area (EEA), we process your personal data in accordance with the General Data Protection Regulation (GDPR). You have the right to lodge a complaint with your local supervisory authority.</p>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="privacy-actions">
                                <Link to="/terms" className="btn-secondary">
                                    <i className="fas fa-file-contract"></i> Terms & Conditions
                                </Link>
                                <Link to="/contact" className="btn-primary">
                                    <i className="fas fa-headset"></i> Contact Support
                                </Link>
                            </div>
                        </div>
                    </main>
                </div>
            </div>

            {/* Custom CSS */}
            <style>{`
                .privacy-page {
                    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
                    min-height: 100vh;
                }

                /* Hero Section */
                .privacy-hero {
                    position: relative;
                    background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
                    padding: 6rem 2rem 2rem;
                    overflow: hidden;
                }

                .hero-pattern {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background-image: url('data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath d="M30 0 L60 30 L30 60 L0 30 Z" fill="rgba(255,255,255,0.03)" /%3E%3C/svg%3E');
                    opacity: 0.5;
                }

                .hero-content {
                    position: relative;
                    max-width: 800px;
                    margin: 0 auto;
                    text-align: center;
                    color: white;
                    z-index: 2;
                }

                .hero-subtitle {
                    display: block;
                    font-size: 1.2rem;
                    text-transform: uppercase;
                    letter-spacing: 3px;
                    margin-bottom: 1rem;
                    opacity: 0.9;
                }

                .hero-title {
                    display: block;
                    font-size: 3.5rem;
                    font-weight: 800;
                    margin-bottom: 1.5rem;
                    text-shadow: 2px 2px 4px rgba(0,0,0,0.2);
                }

                .hero-description {
                    font-size: 1.2rem;
                    max-width: 600px;
                    margin: 0 auto 2rem;
                    opacity: 0.95;
                }

                .last-updated {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.5rem 1.5rem;
                    background: rgba(255,255,255,0.2);
                    backdrop-filter: blur(10px);
                    border-radius: 50px;
                    font-size: 0.9rem;
                    border: 1px solid rgba(255,255,255,0.3);
                }

                .hero-wave {
                    position: absolute;
                    bottom: 0;
                    left: 0;
                    right: 0;
                }

                .hero-wave svg {
                    width: 100%;
                    height: auto;
                    display: block;
                    fill: #f8fafc;
                }

                /* Container */
                .privacy-container {
                    max-width: 1400px;
                    margin: 0 auto;
                    padding: 3rem 2rem;
                }

                /* Grid Layout */
                .privacy-grid {
                    display: grid;
                    grid-template-columns: 280px 1fr;
                    gap: 2.5rem;
                }

                /* Sidebar */
                .privacy-sidebar {
                    position: relative;
                }

                .sidebar-sticky {
                    position: sticky;
                    top: 100px;
                    background: white;
                    border-radius: 20px;
                    padding: 2rem;
                    box-shadow: 0 10px 30px rgba(0,0,0,0.05);
                }

                .sidebar-sticky h3 {
                    font-size: 1.2rem;
                    color: #1e293b;
                    margin-bottom: 1.5rem;
                    padding-bottom: 0.75rem;
                    border-bottom: 2px solid #f1f5f9;
                }

                .sidebar-nav {
                    list-style: none;
                    padding: 0;
                    margin-bottom: 2rem;
                }

                .sidebar-nav li {
                    margin-bottom: 0.75rem;
                }

                .sidebar-nav a {
                    color: #64748b;
                    text-decoration: none;
                    font-size: 0.95rem;
                    transition: all 0.3s;
                    display: block;
                    padding: 0.5rem 1rem;
                    border-radius: 8px;
                }

                .sidebar-nav a:hover {
                    color: #1e3c72;
                    background: #f8fafc;
                    transform: translateX(5px);
                }

                .sidebar-contact {
                    text-align: center;
                    padding-top: 1.5rem;
                    border-top: 1px solid #e2e8f0;
                }

                .sidebar-contact p {
                    color: #64748b;
                    margin-bottom: 1rem;
                }

                .contact-phone,
                .contact-email {
                    display: block;
                    color: #1e293b;
                    text-decoration: none;
                    font-size: 0.9rem;
                    margin: 0.5rem 0;
                    transition: color 0.3s;
                }

                .contact-phone i,
                .contact-email i {
                    color: #1e3c72;
                    margin-right: 0.5rem;
                    width: 20px;
                }

                .contact-phone:hover,
                .contact-email:hover {
                    color: #1e3c72;
                }

                /* Main Content */
                .privacy-main {
                    background: white;
                    border-radius: 30px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.05);
                    overflow: hidden;
                }

                .privacy-content {
                    padding: 3rem;
                }

                /* Sections */
                .privacy-section {
                    margin-bottom: 3rem;
                    padding-bottom: 2rem;
                    border-bottom: 1px solid #e2e8f0;
                    position: relative;
                }

                .privacy-section:last-child {
                    border-bottom: none;
                }

                .section-badge {
                    position: absolute;
                    top: 0;
                    left: 0;
                    font-size: 5rem;
                    font-weight: 800;
                    color: rgba(30,60,114,0.05);
                    line-height: 1;
                    z-index: 0;
                }

                .privacy-section h2 {
                    font-size: 1.8rem;
                    color: #1e293b;
                    margin-bottom: 1.5rem;
                    position: relative;
                    z-index: 1;
                }

                .privacy-section h3 {
                    font-size: 1.3rem;
                    color: #1e293b;
                    margin: 1.5rem 0 1rem;
                }

                .section-content {
                    position: relative;
                    z-index: 1;
                }

                .section-content p {
                    color: #475569;
                    line-height: 1.8;
                    margin-bottom: 1rem;
                }

                /* Lists */
                ul {
                    list-style: none;
                    padding: 0;
                    margin: 1.5rem 0;
                }

                ul li {
                    color: #475569;
                    margin-bottom: 0.75rem;
                    display: flex;
                    align-items: flex-start;
                    gap: 0.75rem;
                }

                ul li i {
                    color: #1e3c72;
                    width: 20px;
                    margin-top: 0.25rem;
                }

                /* Info List */
                .info-list li {
                    padding: 0.5rem;
                    border-radius: 8px;
                    transition: background 0.3s;
                }

                .info-list li:hover {
                    background: #f8fafc;
                }

                /* Usage Grid */
                .usage-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
                    gap: 1.5rem;
                    margin: 2rem 0;
                }

                .usage-card {
                    background: #f8fafc;
                    padding: 1.5rem;
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                    transition: all 0.3s;
                }

                .usage-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 10px 25px rgba(30,60,114,0.1);
                }

                .usage-card i {
                    font-size: 2rem;
                    color: #1e3c72;
                    margin-bottom: 1rem;
                }

                .usage-card h4 {
                    font-size: 1.1rem;
                    color: #1e293b;
                    margin-bottom: 0.5rem;
                }

                .usage-card p {
                    margin: 0;
                    font-size: 0.95rem;
                }

                /* Sharing List */
                .sharing-list li {
                    display: flex;
                    gap: 1rem;
                    padding: 1rem;
                    background: #f8fafc;
                    border-radius: 10px;
                    margin-bottom: 1rem;
                }

                .sharing-list li i {
                    font-size: 1.5rem;
                    color: #1e3c72;
                }

                /* Important Box */
                .important-box {
                    background: #fef2f2;
                    border: 1px solid #fee2e2;
                    border-radius: 10px;
                    padding: 1.5rem;
                    display: flex;
                    gap: 1rem;
                    margin-top: 2rem;
                }

                .important-box i {
                    color: #ef4444;
                    font-size: 1.5rem;
                }

                .important-box h4 {
                    color: #991b1b;
                    margin-bottom: 0.25rem;
                }

                .important-box p {
                    color: #b91c1c;
                    margin: 0;
                }

                /* Cookies Grid */
                .cookies-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 1rem;
                    margin: 2rem 0;
                }

                .cookie-card {
                    background: #f8fafc;
                    padding: 1.5rem;
                    border-radius: 10px;
                    border: 1px solid #e2e8f0;
                    position: relative;
                }

                .cookie-badge {
                    position: absolute;
                    top: 1rem;
                    right: 1rem;
                    background: #1e3c72;
                    color: white;
                    padding: 0.25rem 0.5rem;
                    border-radius: 4px;
                    font-size: 0.7rem;
                }

                .cookie-card h4 {
                    margin-bottom: 0.5rem;
                    padding-right: 4rem;
                }

                /* Security Features */
                .security-features {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
                    gap: 1rem;
                    margin: 2rem 0;
                }

                .security-item {
                    background: #f8fafc;
                    padding: 1rem;
                    border-radius: 8px;
                    text-align: center;
                }

                .security-item i {
                    font-size: 2rem;
                    color: #1e3c72;
                    margin-bottom: 0.5rem;
                    display: block;
                }

                .security-item span {
                    font-size: 0.9rem;
                    font-weight: 500;
                }

                /* Rights List */
                .rights-list {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
                    gap: 0.5rem;
                }

                /* Rights CTA */
                .rights-cta {
                    background: #f0f9ff;
                    border: 1px solid #bae6fd;
                    border-radius: 10px;
                    padding: 1.5rem;
                    text-align: center;
                    margin-top: 2rem;
                }

                .btn-outline {
                    display: inline-block;
                    padding: 0.75rem 1.5rem;
                    background: transparent;
                    border: 2px solid #1e3c72;
                    color: #1e3c72;
                    border-radius: 8px;
                    text-decoration: none;
                    font-weight: 600;
                    margin-top: 1rem;
                    transition: all 0.3s;
                }

                .btn-outline:hover {
                    background: #1e3c72;
                    color: white;
                }

                /* Contact Details */
                .contact-details {
                    background: #f8fafc;
                    border-radius: 15px;
                    padding: 2rem;
                    margin-top: 1.5rem;
                }

                .contact-item {
                    display: flex;
                    gap: 1rem;
                    padding: 1rem 0;
                    border-bottom: 1px solid #e2e8f0;
                }

                .contact-item:last-child {
                    border-bottom: none;
                }

                .contact-item i {
                    font-size: 1.5rem;
                    color: #1e3c72;
                    width: 40px;
                }

                .contact-item a {
                    color: #1e293b;
                    text-decoration: none;
                }

                .contact-item a:hover {
                    color: #1e3c72;
                    text-decoration: underline;
                }

                .contact-item address {
                    font-style: normal;
                    line-height: 1.6;
                }

                /* GDPR Notice */
                .gdpr-notice {
                    background: linear-gradient(135deg, #f0f9ff 0%, #e6f0fa 100%);
                    border-radius: 15px;
                    padding: 2rem;
                    display: flex;
                    gap: 1.5rem;
                    align-items: center;
                    margin: 3rem 0 2rem;
                }

                .gdpr-notice i {
                    font-size: 3rem;
                    color: #1e3c72;
                }

                .gdpr-notice h4 {
                    color: #1e293b;
                    margin-bottom: 0.5rem;
                }

                .gdpr-notice p {
                    color: #475569;
                    margin: 0;
                }

                /* Action Buttons */
                .privacy-actions {
                    display: flex;
                    gap: 1rem;
                    justify-content: center;
                    margin-top: 2rem;
                }

                .btn-secondary,
                .btn-primary {
                    padding: 1rem 2rem;
                    border-radius: 10px;
                    text-decoration: none;
                    font-weight: 600;
                    transition: all 0.3s;
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                .btn-secondary {
                    background: #f1f5f9;
                    color: #475569;
                }

                .btn-secondary:hover {
                    background: #e2e8f0;
                    transform: translateY(-2px);
                }

                .btn-primary {
                    background: linear-gradient(135deg, #1e3c72, #2a5298);
                    color: white;
                }

                .btn-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 10px 25px rgba(30,60,114,0.3);
                }

                /* Note Box */
                .note-box {
                    background: #f0f9ff;
                    border: 1px solid #bae6fd;
                    border-radius: 10px;
                    padding: 1.5rem;
                    display: flex;
                    align-items: flex-start;
                    gap: 1rem;
                    margin: 1.5rem 0;
                }

                .note-box i {
                    color: #0369a1;
                    font-size: 1.5rem;
                }

                .note-box p {
                    color: #0369a1;
                    margin: 0;
                }

                /* Back to Home Link */
                .back-home {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                    margin-top: 2rem;
                    color: #64748b;
                    text-decoration: none;
                    font-size: 0.95rem;
                    transition: color 0.3s;
                }

                .back-home:hover {
                    color: #1e3c72;
                }

                /* Utilities */
                .mt-4 {
                    margin-top: 1.5rem;
                }

                /* Responsive Design */
                @media (max-width: 1024px) {
                    .privacy-grid {
                        grid-template-columns: 1fr;
                    }

                    .sidebar-sticky {
                        position: static;
                        margin-bottom: 2rem;
                    }
                }

                @media (max-width: 768px) {
                    .hero-title {
                        font-size: 2.5rem;
                    }

                    .privacy-container {
                        padding: 2rem 1rem;
                    }

                    .privacy-content {
                        padding: 2rem;
                    }

                    .usage-grid,
                    .cookies-grid,
                    .security-features,
                    .rights-list {
                        grid-template-columns: 1fr;
                    }

                    .gdpr-notice {
                        flex-direction: column;
                        text-align: center;
                    }

                    .privacy-actions {
                        flex-direction: column;
                    }

                    .btn-secondary,
                    .btn-primary {
                        width: 100%;
                        justify-content: center;
                    }
                }

                @media (max-width: 480px) {
                    .hero-title {
                        font-size: 2rem;
                    }

                    .privacy-content {
                        padding: 1.5rem;
                    }

                    .section-badge {
                        font-size: 4rem;
                    }

                    .privacy-section h2 {
                        font-size: 1.5rem;
                    }
                }
            `}</style>
        </div>
    );
};

export default Privacy;
